import { create } from "zustand";
import {
  client,
  getAccessToken,
  refreshAccessToken,
  setAccessToken,
} from "../api/client";

export interface AuthUser {
  id: number;
  email: string;
  role: "admin" | "editor" | "viewer";
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isHydrating: boolean;
  isSubmitting: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<string | void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const extractErrorMessage = (err: unknown, fallback: string): string => {
  const anyErr = err as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = anyErr?.response?.data;
  if (data && typeof data === "object") {
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    for (const value of Object.values(data)) {
      if (Array.isArray(value) && typeof value[0] === "string") {
        return value[0] as string;
      }
      if (typeof value === "string") return value;
    }
  }
  return anyErr?.message ?? fallback;
};

const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isHydrating: true,
  isSubmitting: false,
  error: null,

  hydrate: async () => {
    set({ isHydrating: true });
    try {
      const access = await refreshAccessToken();
      if (!access) {
        set({ user: null, isAuthenticated: false, isHydrating: false });
        return;
      }
      const me = await client.get<AuthUser>("/auth/me/");
      set({ user: me.data, isAuthenticated: true, isHydrating: false });
    } catch {
      setAccessToken(null);
      set({ user: null, isAuthenticated: false, isHydrating: false });
    }
  },

  login: async (email, password) => {
    set({ isSubmitting: true, error: null });
    try {
      const resp = await client.post<{ access: string; user: AuthUser }>(
        "/auth/login/",
        { email, password }
      );
      setAccessToken(resp.data.access);
      set({
        user: resp.data.user,
        isAuthenticated: true,
        isSubmitting: false,
      });
    } catch (err) {
      set({
        error: extractErrorMessage(err, "Sign in failed."),
        isSubmitting: false,
      });
      throw err;
    }
  },

  register: async (email, password) => {
    set({ isSubmitting: true, error: null });
    try {
      const resp = await client.post<{ access?: string; user?: AuthUser; detail?: string }>(
        "/auth/register/",
        { email, password }
      );
      if (resp.data.access && resp.data.user) {
        setAccessToken(resp.data.access);
        set({
          user: resp.data.user,
          isAuthenticated: true,
          isSubmitting: false,
        });
      } else {
        set({ isSubmitting: false });
        return resp.data.detail;
      }
    } catch (err) {
      set({
        error: extractErrorMessage(err, "Registration failed."),
        isSubmitting: false,
      });
      throw err;
    }
  },

  logout: async () => {
    try {
      if (getAccessToken()) {
        await client.post("/auth/logout/");
      }
    } catch {
      // Ignore — we're logging out locally regardless.
    }
    setAccessToken(null);
    set({ user: null, isAuthenticated: false, error: null });
  },

  clearError: () => set({ error: null }),
}));

export default useAuthStore;
